import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, Modal, Stack, TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import mixpanel from "mixpanel-browser";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  usersOverview,
  orgEnabledSensors,
} from "../redux/actions/users/usersData";
import logoImage from "../assets/images/common/doctustechLogo.svg";
import AccessControl from "../components/accessIdentifier/AccessControl";
import AppAutoLogout from "../components/common/AppAutoLogout";
import {
  createSupportTicket,
  logoutUserApi,
} from "../redux/actions/users/usersData";
import { setProfileSliceData } from "../redux/features/profile/profileSlice";
import {
  setOrganisation,
  setSidebarSliceData,
} from "../redux/features/sidebar/sidebarSlice";
import { setUsersSliceData } from "../redux/features/users/usersSlice";
import { mcContactSupportSuccess } from "../utils/mixpanelEvents";
import {
  ACCOUNT,
  AI_SOLUTIONS,
  SIDEBAR_ACCESS_IDENTIFIER,
  SideBarItems,
} from "../utils/sidebar/sidebar";
import LogoutModal from "./modals/LogoutModal";

// Component for sidebar on platform - left side on desktop
const Sidebar = () => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expandItem, setExpandItem] = useState(undefined);
  const [newSidebarFormErr, setNewSidebarFormErr] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const userProfile = useSelector((e) => e.profile.userProfile);
  const userOrganization = useSelector((e) => e.sidebar.organisation);
  const sidebarModalType = useSelector((e) => e.sidebar.sidebarModalType);
  const isSidebarModalOpen = useSelector((e) => e.sidebar.isSidebarModalOpen);
  const isTicketSubmitting = useSelector((e) => e.users.isTicketSubmitting);
  const supportCreateTitle = useSelector((e) => e.users.supportCreateTitle);
  const supportCreateDescription = useSelector(
    (e) => e.users.supportCreateDescription
  );
  const usersSummary = useSelector((e) => e.usersSummary.usersSummary);
  const enabledSensors =
    useSelector((e) => e.usersSummary.enabledSensors) || [];

  const handleSidebarModalClose = () => {
    dispatch(setUsersSliceData({ type: "supportCreateTitle", data: "" }));
    dispatch(setUsersSliceData({ type: "supportCreateDescription", data: "" }));
    dispatch(setSidebarSliceData({ type: "isSidebarModalOpen", data: false }));
    setNewSidebarFormErr(false);
  };
  const handleSidebarModalOpen = (type) => {
    dispatch(setSidebarSliceData({ type: "sidebarModalType", data: type }));
    dispatch(setSidebarSliceData({ type: "isSidebarModalOpen", data: true }));
  };

  const listDataHandler = (e, route) => {
    if (e.toLowerCase() === "log out") {
      handleSidebarModalOpen("logOut");
    } else {
      navigate(route);
    }
  };

  const filteredSolutions = AI_SOLUTIONS.map((data) => {
    if (!data.items) return data;

    const filteredItems = data.items.filter(
      (item) =>
        item.sensorKey === null || enabledSensors.includes(item.sensorKey)
    );

    return { ...data, items: filteredItems };
  }).filter(
    (data) =>
      // hide parent entirely if no sub-items remain (excluding always-visible ones)
      !data.items || data.items.length > 1
  );

  const confirmUserLogout = () => {
    setIsLoggingOut(true);
    dispatch(logoutUserApi())
      .then((e) => {
        if (e?.meta?.requestStatus === "fulfilled") {
          localStorage.removeItem("token");
          localStorage.removeItem("organization");
          dispatch(setOrganisation(null));
          mixpanel.reset();
          dispatch(
            setProfileSliceData({ type: "userProfile", data: undefined })
          );
          handleSidebarModalClose();
          window.location.href = "/";
          setIsLoggingOut(false);
        }
      })
      .catch(() => {
        toast.error("Please try again later.");
      });
  };

  const orgChangeHandler = (event) => {
    dispatch(setOrganisation(event.target.value));
    dispatch(usersOverview({ userOrganization: event.target.value }));
    dispatch(orgEnabledSensors({ userOrganization: event.target.value }));
    localStorage.setItem("organization", event.target.value);
  };

  useEffect(() => {
    SIDEBAR_ACCESS_IDENTIFIER[userProfile?.type].map((item) => {
      const data = SideBarItems[item];
      if (expandItem === undefined && data.items) {
        // check if location.pathname includes any of the sub items
        const isSubItemActive = data.items.some((subItem) =>
          location.pathname.includes(subItem.route)
        );
        if (isSubItemActive) {
          setExpandItem(item);
        }
      }
    });
  }, [location.pathname, userProfile?.type]);

  useEffect(() => {
    if (!userOrganization) return;
    dispatch(usersOverview({ userOrganization }));
    dispatch(orgEnabledSensors({ userOrganization }));
  }, [userOrganization]);

  // Create on submit
  const confirmSupportTicketCreate = () => {
    // Early return if form fields are empty
    const isInvalid =
      !supportCreateTitle.trim() || !supportCreateDescription.trim();
    if (isInvalid) {
      setNewSidebarFormErr(true); // Highlight the invalid fields
      enqueueSnackbar("Please fix the highlighted errors.", {
        variant: "error",
      });
      return;
    }
    dispatch(
      createSupportTicket({
        userOrganization,
        newSupportTicketData: {
          title: supportCreateTitle,
          description: supportCreateDescription,
        },
      })
    )
      .then(({ payload, meta }) => {
        if (meta.requestStatus === "rejected") {
          // Show backend error if available
          enqueueSnackbar(
            payload?.error
              ? payload.error.join(", ")
              : "Something went wrong. Please try later.",
            { variant: "error" }
          );
        } else {
          // Success case
          mcContactSupportSuccess();
          handleSidebarModalClose();
          enqueueSnackbar("Support ticket submitted successfully.", {
            variant: "success",
          });
        }
      })
      .catch(() => {
        // Unexpected errors
        enqueueSnackbar("Something went wrong. Please try later.", {
          variant: "error",
        });
      });
  };

  return (
    <div className="left_sidebar col-lg-2">
      <AppAutoLogout />

      <Modal
        open={isSidebarModalOpen}
        onClose={handleSidebarModalClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description">
        <div className="dt-ta-sidebar-modal-inr">
          {/* Contact support modal */}
          {sidebarModalType === "contactSupport" && (
            <Box className="dt-ta-prog-del-modal" sx={{ width: 400 }}>
              <div className="dt-ta-prog-del-modal-t1">Contact Support</div>
              <div
                className="dt-ta-prog-del-modal-t2"
                style={{ paddingBottom: "32px" }}>
                Please fill up the form and our team will get back to you within
                48 hrs.
              </div>
              <TextField
                id="outlined-basic"
                label="Issue Faced / Feedback"
                error={
                  newSidebarFormErr && supportCreateTitle.trim().length === 0
                }
                helperText={
                  newSidebarFormErr && supportCreateTitle.trim().length === 0
                    ? "Please highlight the issue"
                    : ""
                }
                variant="outlined"
                sx={{ width: "100%", paddingBottom: "24px" }}
                value={supportCreateTitle}
                onChange={(event) => {
                  dispatch(
                    setUsersSliceData({
                      type: "supportCreateTitle",
                      data: event.target.value,
                    })
                  );
                }}
              />
              <TextField
                id="outlined-basic"
                label="More information about the issue"
                error={
                  newSidebarFormErr &&
                  supportCreateDescription.trim().length === 0
                }
                helperText={
                  newSidebarFormErr &&
                  supportCreateDescription.trim().length === 0
                    ? "Please provide more information about the issue"
                    : ""
                }
                variant="outlined"
                multiline
                style={{ width: "100%", paddingBottom: "24px" }}
                rows={3}
                value={supportCreateDescription}
                onChange={(event) => {
                  dispatch(
                    setUsersSliceData({
                      type: "supportCreateDescription",
                      data: event.target.value,
                    })
                  );
                }}
              />
              <Stack
                className="dt-ta-prog-del-modal-btns"
                spacing={2}
                direction="row">
                <Button
                  className="dt-ta-prog-del-modal-btns-out"
                  variant="outlined"
                  onClick={handleSidebarModalClose}>
                  Cancel
                </Button>
                <Button
                  className="dt-ta-prog-del-modal-btns-filled-t2"
                  variant="contained"
                  disabled={isTicketSubmitting}
                  onClick={confirmSupportTicketCreate}>
                  {isTicketSubmitting ? (
                    <div className="dot-flashing dot-flashing-t1 dt-ta-login-cta-ldr"></div>
                  ) : (
                    <span>Submit</span>
                  )}
                </Button>
              </Stack>
            </Box>
          )}

          {/* Logout content */}
          {sidebarModalType === "logOut" && (
            <LogoutModal
              isLoggingOut={isLoggingOut}
              onConfirm={confirmUserLogout}
              onCancel={handleSidebarModalClose}
            />
          )}
        </div>
      </Modal>
      <div className="top_siderBar_div">
        <div className="logo_doct" onClick={() => navigate("/")}>
          <img src={logoImage} alt="img-logo" className="img-fluid img-logo" />
        </div>
        <AccessControl roles={["csm", "client_admin"]}>
          <div className="frm_option_select">
            {userProfile && (
              <Select
                sx={{ width: "100%", backgroundColor: "#ffff" }}
                value={userOrganization}
                onChange={orgChangeHandler}
                defaultValue={userOrganization}
                IconComponent={() => <KeyboardArrowDownIcon />}>
                {userProfile["organizations"]?.map((organization, idx) => (
                  <MenuItem key={idx} value={organization}>
                    {organization.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </div>
        </AccessControl>
      </div>
      <div className="botten_text_sidebar">
        <ul>
          <div className="botten_text_sidebar_opts1">
            {SIDEBAR_ACCESS_IDENTIFIER[userProfile?.type] &&
              SIDEBAR_ACCESS_IDENTIFIER[userProfile?.type].map((item, idx) => {
                const data = SideBarItems[item];
                if (
                  item === "CustomEducation" &&
                  !usersSummary?.configuration?.show_tailored_section
                ) {
                  return null;
                }
                return data ? (
                  <li
                    key={idx}
                    onClick={() => {
                      if (item === "Support") {
                        handleSidebarModalOpen("contactSupport");
                      } else if (data.items) {
                        expandItem === item
                          ? setExpandItem(undefined)
                          : setExpandItem(item);
                      } else {
                        data.route && listDataHandler(data.name, data.route);
                      }
                    }}
                    className={
                      location.pathname.includes(data.route) ||
                      (item === "Support" && isSidebarModalOpen)
                        ? "active"
                        : "sbar_opt_l1"
                    }>
                    {data.items && expandItem === item ? (
                      <>
                        <ul className="sbar_opt_l1_1" style={{ width: "100%" }}>
                          <div className="botten_text_sidebar_opts1">
                            <span style={{ display: "inline-flex" }}>
                              {location.pathname.includes(data.route) ||
                              (item === "Support" && isSidebarModalOpen)
                                ? data.iconActive
                                : data.icon}
                              {data.name}
                            </span>{" "}
                            <span>
                              <KeyboardArrowDownIcon />
                            </span>
                          </div>

                          {data.items.map((item, idx) => {
                            if (
                              item.roles &&
                              !item.roles.includes(userProfile?.type)
                            ) {
                              return null;
                            }

                            return (
                              <li
                                key={idx}
                                onClick={() =>
                                  item.route &&
                                  listDataHandler(item.name, item.route)
                                }
                                className={
                                  location.pathname.includes(item.route)
                                    ? "active"
                                    : "sbar_opt_l1_1_1"
                                }>
                                {item.name}
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    ) : (
                      <>
                        <span style={{ display: "inline-flex" }}>
                          {location.pathname.includes(data.route) ||
                          (item === "Support" && isSidebarModalOpen)
                            ? data.iconActive
                            : data.icon}
                          <span>{data.name}</span>
                        </span>
                        {data.items && (
                          <span>
                            <KeyboardArrowDownIcon />
                          </span>
                        )}
                      </>
                    )}
                  </li>
                ) : null;
              })}
          </div>
          <AccessControl roles={["client_admin"]}>
            {usersSummary?.ai_solutions_enabled &&
              filteredSolutions.length > 0 && (
                <div className="botten_text_sidebar_opts">
                  <h5>DT Sensors</h5>
                  {filteredSolutions.map((data, idx) => {
                    const isExpanded = expandItem === data.id;
                    const hasSubItems = data.items && data.items.length > 0;
                    const isActive = location.pathname.includes(data.route);

                    return (
                      <li
                        key={data.id}
                        onClick={() => {
                          if (hasSubItems) {
                            setExpandItem(isExpanded ? undefined : data.id);
                          } else {
                            listDataHandler(data.name, data.route);
                          }
                        }}
                        className={"sbar_opt_l1"}>
                        {hasSubItems && isExpanded ? (
                          <ul
                            className="sbar_opt_l1_1"
                            style={{ width: "100%", padding: 0 }}>
                            <div
                              className="botten_text_sidebar_opts1"
                              style={{ marginBottom: 0 }}>
                              <span style={{ display: "inline-flex" }}>
                                {isActive ? data.iconActive : data.icon}
                                {data.name}
                              </span>
                              <span>
                                <KeyboardArrowDownIcon
                                  style={{ transform: "rotate(180deg)" }}
                                />
                              </span>
                            </div>

                            {data.items.map((subItem, subIdx) => (
                              <li
                                key={subIdx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  listDataHandler(subItem.name, subItem.route);
                                }}
                                className={
                                  location.pathname.includes(subItem.route)
                                    ? "active"
                                    : "sbar_opt_l1_1_1"
                                }
                                style={{
                                  marginTop: subIdx === 0 ? "10px" : "0px",
                                }}>
                                {subItem.name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <>
                            <span style={{ display: "inline-flex" }}>
                              {isActive ? data.iconActive : data.icon}
                              <span>{data.name}</span>
                            </span>
                            {hasSubItems && (
                              <span>
                                <KeyboardArrowDownIcon />
                              </span>
                            )}
                          </>
                        )}
                      </li>
                    );
                  })}
                </div>
              )}
          </AccessControl>
          <div className="botten_text_sidebar_opts">
            <h5>ACCOUNT</h5>

            {ACCOUNT.map((data) => {
              return (
                <li
                  key={data.id}
                  onClick={() => listDataHandler(data.name, data.route)}
                  className={
                    location.pathname.includes(data.route) ? "active" : ""
                  }>
                  <span>
                    {location.pathname.includes(data.route)
                      ? data.iconActive
                      : data.icon}
                    {data.name}
                  </span>
                </li>
              );
            })}
          </div>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
